/**
 * @author Fernando Ruano
 * @file index.js
 * @description Modal Input Glucometry Component
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  DatePicker,
  Select,
  Row,
  Col,
  TimePicker,
  Input,
  Radio
} from 'antd';
import moment from 'moment';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import ButtonOutlined from '@nephro/components/ButtonOutlined';

// Helpers
import {
  disabledFutureDates,
  disabledFutureHours,
  disabledFutureMinuts
} from 'commons/helpers/utils';

// Styles
import styles from '../../styles.module.css';

const { Option } = Select;

/**
 * Modal Input Glucometry Component.
 * @return { Node } React Modal Input Glucometry Component
 */
const ModalInputsG = ({
  isEditing,
  visible,
  setVisible,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  measurement,
  setMeasurement,
  value,
  setValue,
  onConfirmCreate,
  glucometryTimesList
}) => {
  const { t: translate } = useTranslation();
  const [valueType, setValueType] = useState('#');

  useEffect(() => {
    if (value === '0') {
      setValueType('Low');
    } else if (value === '999') {
      setValueType('High');
    } else {
      setValueType('#');
    }
  }, [visible]);

  const inputLabel = (string) => (
    <h2 className="mt-6 text-sm text-baxterGray text-left">
      {translate(string)}
    </h2>
  );

  const imBadText = 'Me estoy sintiendo mal';
  const hasTimeValidation = measurement?.children === imBadText;
  const validateInputs =
    value === null ||
    !measurement ||
    (hasTimeValidation && !selectedTime) ||
    (value?.length < 2 && valueType !== 'Low');
  return (
    <Modal
      className="records-menu"
      visible={visible}
      title=""
      closable={false}
      footer={null}>
      <div className="text-center">
        <h2 className="mb-0 mt-4 text-2xl font-medium text-baxterGray">
          {translate(
            isEditing ? 'patientRecords.update' : 'patientRecords.create'
          )}
        </h2>
        <h2 className="mb-10 mt-4 text-sm text-baxterGray">
          {translate(
            isEditing
              ? 'patientRecords.updateMeasureG'
              : 'patientRecords.recordMeasureG'
          )}
        </h2>
        {inputLabel('patientRecords.moment')}
        <Select
          showSearch
          value={measurement?.value}
          style={{ width: '100%', textAlign: 'left' }}
          placeholder={translate('patientRecords.select')}
          optionFilterProp="children"
          onSelect={(_, opt) => setMeasurement(opt)}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {glucometryTimesList?.map((itm, idx) => {
            return (
              <Option key={idx} value={itm.id_glucometry_time}>
                {itm.title}
              </Option>
            );
          })}
        </Select>
        <Row gutter={10}>
          <Col xs={hasTimeValidation ? 12 : 24}>
            <>
              {inputLabel('patientRecords.date')}
              <DatePicker
                disabledDate={disabledFutureDates}
                value={selectedDate}
                defaultValue={selectedDate}
                format={'DD/MM/YYYY'}
                onChange={(val) => setSelectedDate(val)}
              />
            </>
          </Col>
          <Col xs={12}>
            {hasTimeValidation && (
              <>
                {inputLabel('Hora')}
                <TimePicker
                  showNow={false}
                  disabledDate={disabledFutureDates}
                  disabledHours={disabledFutureHours}
                  disabledMinutes={disabledFutureMinuts}
                  value={selectedTime || moment()}
                  use12Hours
                  placeholder={translate('patientRecords.select')}
                  format="h:mm a"
                  onChange={(val) => {
                    if (val < moment()) setSelectedTime(val);
                  }}
                />
              </>
            )}
          </Col>
        </Row>
        <Row gutter={10}>
          <Col xs={24}>
            {inputLabel('patientRecords.value')}
            <div className="mb-4">
              <Radio.Group
                onChange={(e) => {
                  const val = e.target.value;
                  setValue(null);
                  if (val === 'Low') {
                    setValue(0);
                  } else if (val === 'High') {
                    setValue(999);
                  }
                  setValueType(val);
                }}
                value={valueType}>
                <Radio.Button value={'Low'}>Low</Radio.Button>
                <Radio.Button value={'#'}>Valor numérico</Radio.Button>
                <Radio.Button value={'High'}>High</Radio.Button>
              </Radio.Group>
            </div>
            {valueType === '#' && (
              <>
                <Input
                  type="number"
                  placeholder="000 mg/dL"
                  value={value}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val?.length <= 3) setValue(val);
                  }}
                />
                {value?.length < 2 && valueType !== 'Low' && (
                  <p className="text-error mt-3 text-left">
                    {translate('patientRecords.lengthError')}
                  </p>
                )}
              </>
            )}
          </Col>
        </Row>
        <div className="w-full flex flex-row justify-center mt-10">
          <ButtonOutlined
            key="cancel"
            type="cyan"
            textButton={translate('commons.cancel')}
            classStyles={[styles.actionButton, 'mr-5'].join(' ')}
            onActionClick={() => setVisible(false)}
          />
          <ButtonFilled
            disabled={validateInputs}
            key="submit"
            type="cyan"
            textButton={translate(
              !isEditing ? 'patientRecords.creation' : 'patientRecords.update'
            )}
            classStyles={[
              styles.actionButton,
              `${validateInputs && 'opacity-40'}`
            ].join(' ')}
            onActionClick={onConfirmCreate}
          />
        </div>
      </div>
    </Modal>
  );
};

/**
 * @name ModalInputsG.propTypes
 * @type { Object }
 */
ModalInputsG.propTypes = {
  isEditing: PropTypes.bool,
  visible: PropTypes.bool,
  setVisible: PropTypes.func,
  selectedDate: PropTypes.any,
  setSelectedDate: PropTypes.func,
  selectedTime: PropTypes.any,
  setSelectedTime: PropTypes.func,
  value: PropTypes.any,
  setValue: PropTypes.func,
  measurement: PropTypes.any,
  setMeasurement: PropTypes.func,
  onConfirmCreate: PropTypes.func,
  glucometryTimesList: PropTypes.any
};

/**
 * Default props
 */
ModalInputsG.defaultProps = {
  mode: 'confirm',
  isEditing: false,
  selectedDate: moment(),
  measurement: null
};

export default ModalInputsG;
